import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';

/**
 * Reusable right-side drawer for editing material status
 *
 * Props:
 * - isOpen: boolean - controls drawer visibility
 * - onClose: () => void - callback when drawer closes
 * - job: object - the job/work order data (must have id, part_number, material_status, material_notes)
 * - onUpdate: (updatedJob) => void - callback when data is updated
 */
export default function MaterialDrawer({
  isOpen,
  onClose,
  job,
  onUpdate,
}) {
  // Job material state
  const [materialStatus, setMaterialStatus] = useState('');
  const [materialNotes, setMaterialNotes] = useState('');

  // Part material state
  const [partMaterial, setPartMaterial] = useState('');
  const [originalPartMaterial, setOriginalPartMaterial] = useState('');
  const [loadingPart, setLoadingPart] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when job changes
  useEffect(() => {
    if (job) {
      setMaterialStatus(job.material_status || '');
      setMaterialNotes(job.material_notes || '');
    }
  }, [job]);

  // Fetch part material when drawer opens
  useEffect(() => {
    if (isOpen && job?.part_number) {
      fetchPartMaterial();
    }
  }, [isOpen, job?.part_number]);

  const fetchPartMaterial = async () => {
    if (!job?.part_number) return;

    setLoadingPart(true);
    try {
      const { data, error } = await supabase
        .from('parts')
        .select('material')
        .eq('part_number', job.part_number)
        .single();

      if (error) throw error;
      setPartMaterial(data?.material || '');
      setOriginalPartMaterial(data?.material || '');
    } catch (err) {
      console.error('Error fetching part material:', err);
      setPartMaterial('');
      setOriginalPartMaterial('');
    } finally {
      setLoadingPart(false);
    }
  };

  const handleSave = async () => {
    if (!job) return;

    setIsSaving(true);
    try {
      // Update job material status and notes
      const { error: jobError } = await supabase
        .from('jobs')
        .update({
          material_status: materialStatus || null,
          material_notes: materialNotes || '',
        })
        .eq('id', job.id);

      if (jobError) throw new Error(`Failed to update job: ${jobError.message}`);

      // Update part material if changed
      if (partMaterial !== originalPartMaterial && job.part_number) {
        const { error: partError } = await supabase
          .from('parts')
          .update({ material: partMaterial || null })
          .eq('part_number', job.part_number);

        if (partError) throw new Error(`Failed to update part material: ${partError.message}`);
        setOriginalPartMaterial(partMaterial);
      }

      // Notify parent of update
      onUpdate?.({
        ...job,
        material_status: materialStatus || null,
        material_notes: materialNotes || '',
      });

      toast.success('Material info updated');
      onClose();
    } catch (err) {
      console.error('Error saving material:', err);
      toast.error(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    if (job) {
      setMaterialStatus(job.material_status || '');
      setMaterialNotes(job.material_notes || '');
    }
    setPartMaterial(originalPartMaterial);
    onClose();
  };

  if (!job) return null;

  const statusOptions = [
    { value: '', label: 'Not Set', color: 'bg-slate-100 text-slate-500' },
    { value: 'no_stock', label: 'No Stock', color: 'bg-red-100 text-red-700' },
    { value: 'ordered', label: 'Ordered', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'ready', label: 'Ready', color: 'bg-green-100 text-green-700' },
  ];

  const currentStatus = statusOptions.find(s => s.value === materialStatus) || statusOptions[0];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-sm">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-base font-semibold text-slate-900">
                          Material Info
                        </Dialog.Title>
                        <button
                          onClick={handleClose}
                          className="p-1 rounded-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {job.po_number}{job.po_line_number ? `-${job.po_line_number}` : ''} • {job.part_number}
                      </p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      {/* Job Material Status Section */}
                      <div className="mb-6">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
                          Job Material Status
                        </h3>

                        {/* Status Pills */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {statusOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setMaterialStatus(option.value)}
                              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-all ${
                                materialStatus === option.value
                                  ? `${option.color} ring-2 ring-slate-400 ring-offset-1`
                                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                              }`}
                            >
                              {option.value && (
                                <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                                  option.value === 'no_stock' ? 'bg-red-500' :
                                  option.value === 'ordered' ? 'bg-yellow-500' :
                                  option.value === 'ready' ? 'bg-green-500' : ''
                                }`} />
                              )}
                              {option.label}
                            </button>
                          ))}
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Notes
                          </label>
                          <textarea
                            value={materialNotes}
                            onChange={(e) => setMaterialNotes(e.target.value)}
                            placeholder="Add notes about material status..."
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
                          />
                        </div>
                      </div>

                      {/* Part Material Section */}
                      <div className="mb-6">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
                          Part Material Specification
                        </h3>

                        {loadingPart ? (
                          <p className="text-sm text-slate-500">Loading...</p>
                        ) : (
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Material
                            </label>
                            <input
                              type="text"
                              value={partMaterial}
                              onChange={(e) => setPartMaterial(e.target.value)}
                              placeholder="e.g., 6061-T6 Aluminum, 304 Stainless..."
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            />
                            <p className="text-xs text-slate-400 mt-1">
                              This updates the part's material field in the parts database.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Quick Info */}
                      <div className="p-3 bg-slate-50 rounded-sm border border-slate-100">
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Current Status
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-sm font-medium ${currentStatus.color}`}>
                            {currentStatus.value && (
                              <span className={`w-2 h-2 rounded-full ${
                                currentStatus.value === 'no_stock' ? 'bg-red-500' :
                                currentStatus.value === 'ordered' ? 'bg-yellow-500' :
                                currentStatus.value === 'ready' ? 'bg-green-500' : ''
                              }`} />
                            )}
                            {currentStatus.label}
                          </span>
                          {materialNotes && (
                            <span className="text-xs text-slate-500 truncate flex-1">
                              "{materialNotes}"
                            </span>
                          )}
                        </div>
                        {partMaterial && (
                          <p className="text-xs text-slate-600 mt-2">
                            <span className="text-slate-400">Part Material:</span> {partMaterial}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
                      <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
